import { IBridgeIdentity } from '@/interfaces/print-bridge-protocol';

export const BRIDGE_PORTS = [3040, 3041, 3042, 3043] as const;
const DETECT_TIMEOUT_MS = 3000;

/**
 * Chrome Local Network Access: non-standard fetch field, typed locally so no
 * `as any` is needed.
 */
interface IRequestInitWithTargetAddressSpace extends RequestInit {
  targetAddressSpace?: 'loopback' | 'local' | 'private' | 'public';
}

export interface IDetectedBridge {
  identity: IBridgeIdentity;
  port: number;
}

async function probePort(
  port: number,
  signal: AbortSignal
): Promise<IDetectedBridge | null> {
  const init: IRequestInitWithTargetAddressSpace = {
    signal,
    targetAddressSpace: 'loopback',
  };

  try {
    const res = await fetch(
      `http://127.0.0.1:${port}/printbridge/v1/identity`,
      init
    );
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<IBridgeIdentity>;
    if (body?.product !== 'print-bridge' || typeof body.version !== 'string')
      return null;
    return { identity: body as IBridgeIdentity, port };
  } catch {
    // Network error, LNA permission prompt/denial, or abort: treat as "no
    // bridge on this port" — the 30s re-probe loop recovers after the user
    // grants permission.
    return null;
  }
}

/**
 * Parallel identity probes on ports 3040-3043 with a 3s overall timeout; the
 * first (lowest-port) valid `print-bridge` response wins.
 */
export async function detectBridge(): Promise<IDetectedBridge | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DETECT_TIMEOUT_MS);

  try {
    const results = await Promise.all(
      BRIDGE_PORTS.map((port) => probePort(port, controller.signal))
    );
    return results.find((r) => r !== null) ?? null;
  } finally {
    clearTimeout(timer);
    controller.abort();
  }
}
